import React, { useCallback, useContext, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useAppCreateReferralContext } from '@unite-us/app-create-referral';
import { getAuthToken, coreApi } from 'src/api/config';
import { env } from 'src/config/env';
import { TrackerContext } from '@unite-us/client-utils';
import { ComponentLibraryLoader, useToast } from '@unite-us/app-components';
import { connect } from 'react-redux';
import { getEnumsFromState } from 'common/utils/Enums';
import { browserHistory } from 'common/utils/browserHistory';
import PropTypes from 'prop-types';
import { useSelectedPrograms } from '@unite-us/app-search';
import { omitBy, isNil, isString, isEmpty } from 'lodash';
import {
  serializeQueryParams,
} from '@unite-us/json-api-resources/dist/utils/serializeQueryParams';
import { ClientHeader } from '@unite-us/app-client-profile';
import { updateGlobalState } from 'src/actions/Global/globalActions';
import ClientsDrawer from 'src/components/ClientsDrawer';
import getClientsDetailLink from 'src/common/utils/getClientsDetailLink';
import { goToCase } from 'src/components/Cases/utils/routing';
import {
  crtb1121HideFieldInForm,
  pays5590RefactorInsuranceFilters as pays5590RefactorInsuranceFiltersSelector,
  crtb2182ClarifyReferralLanguage as crtb2182ClarifyReferralLanguageSelector,
  crtb2130MultipleClientNeeds as crtb2130MultipleClientNeedsSelector,
  pays11587AtomicReferralCreation as pays11587AtomicReferralCreationSelector,
} from '../../../common/utils/FeatureFlags/flags';
import ConsentReferrals from '../../../components/InformedConsent/components/ConsentReferrals';

const getBasename = ({ pathname, route, basename = '' }) => {
  const path = pathname.split(route).at(0);
  return `${basename}${path}${route}`;
};

const ShareDrawerLoader = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/shares-utils'),
  component: 'ShareDrawer',
  loadingApp: 'app-client',
  source: 'shares-utils',
  datadogRum,
});

const AppReferralsLoader = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-create-referral'),
  component: 'AppReferrals',
  loadingApp: 'app-client',
  source: 'app-create-referral',
  datadogRum,
});

function Referrals({
  location,
  currentEmployee,
  currentProvider,
  enums,
  initialAddresses,
  crtb1121HideFieldInFormEnabled,
  crtb1239AdaptDraftReferralFlow,
  pays5590RefactorInsuranceFilters,
  pays11587AtomicReferralCreation,
  crtb2182ClarifyReferralLanguage,
  crtb2130MultipleClientNeeds,
}) {
  const { addToast } = useToast();
  const basename = getBasename({
    basename: location.basename,
    pathname: location.pathname,
    route: 'referrals/2',
  });

  const [showHeader, setShowHeader] = useState(true);
  const [drawerClient, setDrawerClient] = useState(null);
  const [isClientsDrawerOpen, setIsClientsDrawerOpen] = useState(false);

  const trackEvent = useContext(TrackerContext);

  const {
    state: {
      person: maybePerson,
      assistanceRequestId: assistanceRequest,
      resourceListId,
      referrals,
      formSubmissionId,
      serviceIds,
      workflow,
      isForwardReferral,
      caseId: caseIdFromContext,
    },
  } = useAppCreateReferralContext();

  const { dispatchAddPrograms, dispatchRemoveAllPrograms, dispatchRemoveProgram } = useSelectedPrograms();

  const personId = isString(maybePerson) ? maybePerson : maybePerson?.id;
  const assistanceRequestId = isString(assistanceRequest) ? assistanceRequest : assistanceRequest?.id;
  const isSearchWorkflow = workflow === 'search';
  const isResourceWorkflow = !isEmpty(resourceListId);

  // For now we build the programs object to match the app-search context
  const buildProgramDetails = (program, referral) => ({
    id: program.id,
    name: program.name,
    providerId: program.provider.id,
    providerLicensed: program.provider.licensed,
    providerName: program.provider.name,
    receivingReferrals: program.receiving_referrals,
    service: referral.service.id,
    status: program.status,
  });

  const syncSelectedProgramsWithReferrals = () => {
    dispatchRemoveAllPrograms();
    const programs = referrals.flatMap((r) => r.programs.map((p) => buildProgramDetails(p, r)));
    if (programs.length > 0) {
      dispatchAddPrograms(programs);
    }
  };

  const buildSearchWorkflowSearch = () => {
    const forwardReferralParams = {};
    // Extract additional parameters from the current URL if the flag is enabled
    const currentQueryParams = new URLSearchParams(browserHistory.getCurrentLocation().search);

    // Define the parameters to forward
    ['referral-id', 'sensitive', 'referral-type', 'service-type-name'].forEach((key) => {
      const value = currentQueryParams.get(key);
      if (value !== null && value !== undefined) {
        forwardReferralParams[key] = value;
      }
    });

    // Create the existing search query parameters as an object
    const searchQueryParams = serializeQueryParams(omitBy({
      person: personId,
      resource_list: resourceListId,
      form_submission: formSubmissionId,
      assistance_request: assistanceRequestId,
      services: serviceIds?.join(','),
      caseId: caseIdFromContext,
      ...forwardReferralParams,
    }, isNil));

    return `?${searchQueryParams}&preserve_cart=true`;
  };

  const navigateBack = useCallback((params) => {
    const { addSelectClient, referralId, referralDescription } = params || {};

    if (isSearchWorkflow) {
      syncSelectedProgramsWithReferrals();
      // Define state based on whether addSelectClient is true
      const state = addSelectClient ?
        { source: 'client', referralId, referralDescription } :
        { referralId, referralDescription };
      browserHistory.replace({
        pathname: '/referrals/create/add-resources',
        search: buildSearchWorkflowSearch(),
        state,
      });
      return;
    }

    if (isResourceWorkflow) {
      browserHistory.replace({
        pathname: `/facesheet/${personId}/resource-lists/${resourceListId}`,
      });
      return;
    }

    browserHistory.goBack();
  });

  const navigateToAddResources = useCallback(() => {
    syncSelectedProgramsWithReferrals();

    const pathToSearch = '/referrals/create/add-resources';
    const searchQueryParams = serializeQueryParams(omitBy({
      person: personId,
    }, isNil));

    browserHistory.replace({
      pathname: pathToSearch,
      search: `?${searchQueryParams}&preserve_cart=true`,
    });
  });

  return (
    <>
      <div className="mb-6 bg-white -mx-container-padding">
        {showHeader && (
          <ClientHeader
            adapters={{ coreApi }}
            appState={{
              providerId: currentProvider?.id,
              personId,
              enums,
              crtb854ForwardingSuperset: true,
              isForwardReferral,
            }}
            callbacks={{
              onClientNameClick: (client) => {
                setDrawerClient(client);
                setIsClientsDrawerOpen(!isClientsDrawerOpen);
              },
              getClientsDetailLink,
            }}
            mode="lite"
          />
        )}
      </div>
      <AppReferralsLoader
        appSettings={
          {
            env: {
              getAuthToken,
              coreUrl: env.CORE_API,
              employeeId: currentEmployee?.id,
              providerId: currentEmployee?.provider.id,
              sharesUrl: env.SHARES_URL,
              consentUrl: env.CONSENT_APP_URL,
              googleMapsApiKey: env.GOOGLE_MAPS_API_KEY,
              source: 'app-client',
            },
            feature_flags: {
              crtb1121HideFieldInFormEnabled,
              crtb1239AdaptDraftReferralFlow,
              crtb854ForwardingSuperset: true,
              hint542SupersetUnlistedPrograms: true,
              crtb2182ClarifyReferralLanguage,
              pays5590RefactorInsuranceFilters,
              pays11587AtomicReferralCreation,
              crtb2130MultipleClientNeeds,
              // TODO(PAYS-11733): Remove this once the flag is removed from the app-create-referral package.
              pays5650UpdateEditAuthorizations: true,
            },
            basename,
            initialAddresses,
          }
        }
        appState={{
          currentEmployee,
          currentProvider,
          enums,
          source: 'app-client',
        }}
        callbacks={{
          notify: {
            error: (message) => addToast({
              message,
              type: 'error',
            }),
            success: (message) => addToast({
              message,
              type: 'success',
            }),
            warn: (message) => addToast({
              message,
              type: 'warning',
            }),
          },
          trackEventCallback: trackEvent,
          consent: {
            updateGlobalState,
          },
          onReferralCompleted: () => {
            browserHistory.push({
              pathname: `/facesheet/${personId}`,
            });
          },
          onSaveDraftReferral: () => {
            browserHistory.push({
              pathname: '/dashboard/referrals/sent/draft',
            });
          },
          onDeselectProgram: isSearchWorkflow ? dispatchRemoveProgram : () => { },
          // TODO: No need for both of these callbacks.
          onNavigateBack: navigateBack,
          onNavigateToAddResources: navigateToAddResources,
          onSubmitConsent: async () => {
            // refresh ClientHeader to refetch person to show new consent status
            setShowHeader(false);
            setTimeout(() => {
              setShowHeader(true);
            });
          },
          redirectToOffPlatformCaseDetails: (caseId) => {
            goToCase({ id: caseId, state: 'off_platform', person: { id: personId } }, {}, '', { source: 'referral' });
          },
          redirectToReferralDetails: (referralId) => {
            browserHistory.push({
              pathname: `/dashboard/referrals/sent/all/${referralId}`,
              state: { source: 'referral' },
            });
          },
          redirectToOutboundReferralsAll: () => {
            browserHistory.push({
              pathname: '/dashboard/referrals/sent/all',
              state: { source: 'referral' },
            });
          },
          redirectToDraftReferral: (caseId) => {
            goToCase({ id: caseId, state: 'draft', person: { id: personId } });
          },
          onReferralReload: () => {
            browserHistory.push({
              pathname: `/facesheet/${personId}`,
            });
          },
        }}
        components={{
          consent: ConsentReferrals,
          ShareDrawer: ShareDrawerLoader,
        }}
      />
      {drawerClient && (
        <ClientsDrawer
          open={isClientsDrawerOpen}
          contact={drawerClient}
          onClose={() => setIsClientsDrawerOpen(false)}
        />
      )}
    </>
  );
}

const getInitialAddresses = (state) => {
  const { globalState, contacts, selectedContact } = state;
  const { currentEmployee } = globalState;

  const userAddresses = currentEmployee?.addresses;
  const groupAddresses = currentEmployee?.provider?.addresses;
  const contact = contacts?.contacts?.find((contactItem) => contactItem.id === selectedContact);

  return {
    client: contact?.addresses,
    user: userAddresses,
    ours: groupAddresses,
  };
};

const getFeatureFlags = (state) => ({
  crtb1121HideFieldInFormEnabled: crtb1121HideFieldInForm(state),
  pays5590RefactorInsuranceFilters: pays5590RefactorInsuranceFiltersSelector(state),
  pays11587AtomicReferralCreation: pays11587AtomicReferralCreationSelector(state),
  crtb2182ClarifyReferralLanguage: crtb2182ClarifyReferralLanguageSelector(state),
  crtb2130MultipleClientNeeds: crtb2130MultipleClientNeedsSelector(state),
});

function mapStateToProps(state) {
  const { globalState, session } = state;
  const { currentEmployee, currentProvider } = globalState;

  const nhConsent = currentProvider?.group?.features?.nh_consent ||
    session?.currentProvider?.features?.nh_consent;

  return {
    currentEmployee,
    currentProvider: {
      ...currentProvider?.group,
      nh_consent: nhConsent,
    },
    enums: getEnumsFromState(state),
    initialAddresses: getInitialAddresses(state),
    ...getFeatureFlags(state),
  };
}

// Should match shape of address validator in Core
const addressShape = {
  address_type: PropTypes.string.isRequired,
  line_1: PropTypes.string,
  line_2: PropTypes.string,
  city: PropTypes.string.isRequired,
  county: PropTypes.string,
  state: PropTypes.string.isRequired,
  postal_code: PropTypes.string,
  country: PropTypes.string.isRequired,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  is_primary: PropTypes.bool,
};

Referrals.propTypes = {
  currentEmployee: PropTypes.object.isRequired,
  currentProvider: PropTypes.object.isRequired,
  enums: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  crtb1121HideFieldInFormEnabled: PropTypes.bool.isRequired,
  crtb1239AdaptDraftReferralFlow: PropTypes.bool.isRequired,
  initialAddresses: PropTypes.shape({
    CLIENT: PropTypes.arrayOf(PropTypes.shape(addressShape)),
    USER: PropTypes.arrayOf(PropTypes.shape(addressShape)),
    GROUP: PropTypes.arrayOf(PropTypes.shape(addressShape)),
  }).isRequired,
  pays5590RefactorInsuranceFilters: PropTypes.bool.isRequired,
  pays11587AtomicReferralCreation: PropTypes.bool.isRequired,
  crtb2130MultipleClientNeeds: PropTypes.bool.isRequired,
  crtb2182ClarifyReferralLanguage: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Referrals);
